import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import apiClient from '../api/api-client'
import { AxiosResponse } from 'axios'

import { SignUpData, User, UserCollection } from '../model'
import { FetchParams } from '@/js/utils/fetch-params'

export const useUserStore = defineStore('user', () => {
    const users = ref<Array<User>>([])
    const loadingArray = ref<Array<string>>([])
    const errorArray = ref({ fetch: false, change: false, create: false })
    const isLoading = computed(() => loadingArray.value.length > 0)
    const hasErrors = computed(() => Object.values(errorArray.value).some((error) => error))
    const getErrors = computed(() => errorArray)
    const getUsers = computed(() => users)

    const getUserById = (userId?: string) => users.value.find((user) => user.userId === userId)
    const getUserByEmail = computed(() => {
        return (email: string) => {
            return users.value.find((user) => {
                return user.email?.email === email
            })
        }
    })

    const getUsersForAutocompletion = computed(() => {
        return users.value
            .map((user: User) => {
                return {
                    id: user.userId,
                    label: `${user.titel?.trim()} ${user.vorname?.trim()} ${user.nachname?.trim()} (${user.email?.email?.trim()})`
                }
            })
            .sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1))
    })

    const replaceUserInState = (newUser: User) => {
        const index = users.value.findIndex((user) => user.userId === newUser.userId)
        users.value.splice(index, 1, newUser)
    }

    async function fetchPaginatedUsers(
        params: FetchParams
    ): Promise<AxiosResponse<UserCollection, any> | undefined> {
        loadingArray.value.push('fetchPaginatedUsers')
        errorArray.value.fetch = false

        try {
            const url = '/api/users'
            const result = await apiClient.get(url, {
                params
            })

            for (const user of result.data['hydra:member']) {
                replaceUserInState(user)
            }

            return result.data
        } catch (error: any) {
            errorArray.value.fetch = error.response || error
            return undefined
        } finally {
            const index = loadingArray.value.indexOf('fetchPaginatedUsers')
            if (index !== -1) loadingArray.value.splice(index, 1)
        }
    }

    async function saveUser(userId: string, userData: object): Promise<void> {
        loadingArray.value.push('saveUser')
        errorArray.value.change = false
        try {
            const response: AxiosResponse<User, any> = await apiClient.patch(
                `/api/users/${userId}`,
                {
                    userId,
                    ...userData
                },
                { headers: { 'Content-Type': 'application/merge-patch+json' } }
            )
            replaceUserInState(response.data)
        } catch (error: any) {
            errorArray.value.change = error.response
        } finally {
            loadingArray.value.splice(loadingArray.value.indexOf('saveUser'), 1)
        }
    }

    async function createUser(userData: SignUpData): Promise<void> {
        loadingArray.value.push('createUser')
        errorArray.value.change = false
        try {
            const response: AxiosResponse<User, any> = await apiClient.post(`/api/users/signup`, {
                titel: userData?.titel,
                vorname: userData?.vorname,
                nachname: userData?.nachname,
                email: userData?.email,
                gender: userData?.gender
            })
            users.value = [response.data, ...users.value]
        } catch (error: any) {
            errorArray.value.change = error.response
        } finally {
            loadingArray.value.splice(loadingArray.value.indexOf('createUser'), 1)
        }
    }

    async function removeUser(user: User): Promise<void> {
        loadingArray.value.push('removeUser')
        errorArray.value.change = false
        try {
            await apiClient.post(`/api/users/remove`, {
                user: user['@id']
            })
            const index = users.value.findIndex((currentUser) => currentUser.userId === user.userId)
            if (index !== -1) {
                users.value.splice(index, 1)
            }
        } catch (error: any) {
            errorArray.value.change = error.response
        } finally {
            loadingArray.value.splice(loadingArray.value.indexOf('createUser'), 1)
        }
    }

    async function removeRolle(userId: string, rollenName: string): Promise<void> {
        loadingArray.value.push('removeRolle')
        errorArray.value.change = false
        try {
            const response: AxiosResponse<User, any> = await apiClient.post(
                `/api/users/remove-rolle`,
                {
                    user: `/api/users/${userId}`,
                    rollenName
                }
            )
            replaceUserInState(response.data)
        } catch (error: any) {
            errorArray.value.change = error.response
        } finally {
            loadingArray.value.splice(loadingArray.value.indexOf('removeRolle'), 1)
        }
    }

    async function replaceRolle(
        userId: string,
        rollenName: string,
        fachbereichIds: string[],
        studiengangIds: string[],
        modulIds: string[],
        isForAll: boolean
    ): Promise<void> {
        loadingArray.value.push('replaceRolle')
        errorArray.value.change = false

        try {
            const response: AxiosResponse<User, any> = await apiClient.post(
                `/api/users/replace-rolle`,
                {
                    user: `/api/users/${userId}`,
                    rollenName: rollenName,
                    fachbereiche: fachbereichIds.map((id: string) => `/api/fachbereiche/${id}`),
                    studiengaenge: studiengangIds.map((id: string) => `/api/studiengaenge/${id}`),
                    module: modulIds.map((id: string) => `/api/module/${id}`),
                    isForAll
                }
            )
            replaceUserInState(response.data)
        } catch (error: any) {
            errorArray.value.change = error.response
        } finally {
            loadingArray.value.splice(loadingArray.value.indexOf('removeRolle'), 1)
        }
    }

    async function fetchUsers(): Promise<void> {
        loadingArray.value.push('fetch')
        errorArray.value.fetch = false
        users.value = []
        const url = '/api/users'
        try {
            const response: AxiosResponse<UserCollection, any> = await apiClient.get(url, {
                params: {
                    pagination: false
                }
            })
            users.value = response.data['hydra:member']
        } catch (error: any) {
            errorArray.value.fetch = error.response
        } finally {
            loadingArray.value.splice(loadingArray.value.indexOf('fetch'), 1)
        }
    }

    return {
        users,
        loadingArray,
        errorArray,
        isLoading,
        hasErrors,
        getErrors,
        getUsers,
        getUserById,
        getUserByEmail,
        getUsersForAutocompletion,
        fetchUsers,
        fetchPaginatedUsers,
        saveUser,
        createUser,
        removeUser,
        removeRolle,
        replaceRolle
    }
})
